import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PointerEvent as ReactPointerEvent, ClipboardEvent as ReactClipboardEvent } from 'react';

// Rajzfelület az ügynöknek szánt vázlathoz: egyszerű „paint” — toll, radír, néhány szín és vastagság, visszavonás, törlés, háttér-kép.
// Két réteg: a háttér (fehér vagy a kapott/beillesztett kép) és a rajz (átlátszó) — a radír csak a rajzot törli, a háttér marad.
// Kész: a két réteg egy PNG-fájlba, amit a hívó a szövegmezőhöz csatol (a szöveg a mezőben megy, mint a vágólapnál).
const COLORS = ['#ff5c1a', '#ff2d2d', '#ffd400', '#3dff7a', '#39b6ff', '#ffffff', '#111111'];
const SIZES = [3, 7, 14];
const MAX_SIDE = 2000;
const UNDO_MAX = 40;

interface Props { image?: File | null; onDone: (file: File) => void; onCancel: () => void }

export function DrawPad({ image = null, onDone, onCancel }: Props) {
  const bgRef = useRef<HTMLCanvasElement>(null);
  const drawRef = useRef<HTMLCanvasElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const bgImg = useRef<HTMLImageElement | null>(null);
  const [size, setSize] = useState({ w: 1200, h: 750 });
  const [fit, setFit] = useState({ w: 1200, h: 750 });
  const [color, setColor] = useState<string>(COLORS[0] ?? '#ff5c1a');
  const [width, setWidth] = useState<number>(SIZES[1] ?? 7);
  const [eraser, setEraser] = useState(false);
  const [undoN, setUndoN] = useState(0);
  const [bgName, setBgName] = useState<string | null>(null);
  const undo = useRef<ImageData[]>([]);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  // Háttér képből: a vászon mérete a képé (legfeljebb MAX_SIDE); a rajz-réteg és a visszavonás tiszta lappal indul
  const loadBackground = useCallback((file: File) => {
    const url = URL.createObjectURL(file); const img = new Image();
    img.onload = () => {
      const k = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
      bgImg.current = img; setBgName(file.name); undo.current = []; setUndoN(0);
      setSize({ w: Math.max(1, Math.round(img.naturalWidth * k)), h: Math.max(1, Math.round(img.naturalHeight * k)) });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => { URL.revokeObjectURL(url); alert('Ezt a képet nem tudom betölteni.'); };
    img.src = url;
  }, []);
  useEffect(() => { if (image) loadBackground(image); }, [image, loadBackground]);

  // A méret változása után (a canvas attribútuma törli a tartalmat): fehér háttér + a kép, a rajz-réteg üres
  useEffect(() => {
    const bg = bgRef.current?.getContext('2d'); if (!bg) return;
    bg.fillStyle = '#ffffff'; bg.fillRect(0, 0, size.w, size.h);
    if (bgImg.current) bg.drawImage(bgImg.current, 0, 0, size.w, size.h);
    drawRef.current?.getContext('2d')?.clearRect(0, 0, size.w, size.h);
  }, [size]);

  // A vászon a rendelkezésre álló helyre illesztve, méretarányosan
  useEffect(() => {
    const el = areaRef.current; if (!el) return;
    const place = () => { const r = el.getBoundingClientRect(); const k = Math.min((r.width - 24) / size.w, (r.height - 24) / size.h, 1.5); setFit({ w: Math.max(80, Math.floor(size.w * k)), h: Math.max(50, Math.floor(size.h * k)) }); };
    place(); const ro = new ResizeObserver(place); ro.observe(el); return () => ro.disconnect();
  }, [size]);

  const pos = (e: ReactPointerEvent<HTMLCanvasElement>) => { const c = e.currentTarget; const r = c.getBoundingClientRect(); return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) }; };
  const pushUndo = () => { const c = drawRef.current; const g = c?.getContext('2d'); if (!c || !g) return; undo.current.push(g.getImageData(0, 0, c.width, c.height)); if (undo.current.length > UNDO_MAX) undo.current.shift(); setUndoN(undo.current.length); };
  const stroke = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const g = drawRef.current?.getContext('2d'); if (!g) return;
    g.lineCap = 'round'; g.lineJoin = 'round'; g.lineWidth = eraser ? width * 3 : width;
    g.globalCompositeOperation = eraser ? 'destination-out' : 'source-over'; g.strokeStyle = color;
    g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke();
  };
  const onDown = (e: ReactPointerEvent<HTMLCanvasElement>) => { if (e.pointerType === 'mouse' && e.button !== 0) return; e.currentTarget.setPointerCapture(e.pointerId); pushUndo(); drawing.current = true; const p = pos(e); last.current = p; stroke(p, p); };
  const onMove = (e: ReactPointerEvent<HTMLCanvasElement>) => { if (!drawing.current || !last.current) return; const p = pos(e); stroke(last.current, p); last.current = p; };
  const onUp = () => { drawing.current = false; last.current = null; };
  const doUndo = useCallback(() => { const d = undo.current.pop(); const g = drawRef.current?.getContext('2d'); if (!d || !g) return; g.putImageData(d, 0, 0); setUndoN(undo.current.length); }, []);
  const clear = () => { pushUndo(); drawRef.current?.getContext('2d')?.clearRect(0, 0, size.w, size.h); };
  const finish = () => {
    const out = document.createElement('canvas'); out.width = size.w; out.height = size.h; const g = out.getContext('2d');
    if (!g || !bgRef.current || !drawRef.current) return;
    g.drawImage(bgRef.current, 0, 0); g.drawImage(drawRef.current, 0, 0);
    out.toBlob((blob) => { if (blob) onDone(new File([blob], `rajz-${Date.now()}.png`, { type: 'image/png' })); }, 'image/png');
  };
  const pasteBg = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const it of items) { const t = it.types.find((x) => x.startsWith('image/')); if (t) { const blob = await it.getType(t); loadBackground(new File([blob], `vagolap.${t.split('/')[1] === 'jpeg' ? 'jpg' : t.split('/')[1]}`, { type: t })); return; } }
      alert('A vágólapon nincs kép.');
    } catch { alert('Nem férek a vágólaphoz — engedélyezd a böngészőben, vagy Ctrl+V itt.'); }
  };
  const onPaste = (e: ReactClipboardEvent) => { const f = Array.from(e.clipboardData?.files || []).find((x) => x.type.startsWith('image/')); if (f) { e.preventDefault(); loadBackground(f); } };
  // Esc = mégse, Ctrl+Z = visszavonás
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onCancel(); } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); doUndo(); } };
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, doUndo]);

  // Portál a body-ba: a projekt-oldal (és a mobil fejléc/alsó sáv) rétegei fölé, a lap átalakításaitól függetlenül
  return createPortal(
    <div className="a-draw a-overlay fixed inset-0 flex flex-col" onPaste={onPaste} role="dialog" aria-label="Rajzolás">
      <div className="a-draw-bar">
        <span className="a-draw-title">RAJZOLÁS</span>
        <div className="a-draw-colors" title="Szín">{COLORS.map((c) => <button key={c} type="button" className={`sw${color === c && !eraser ? ' on' : ''}`} style={{ background: c }} aria-label={c} onClick={() => { setColor(c); setEraser(false); }} />)}</div>
        <div className="a-draw-sizes" title="Vastagság">{SIZES.map((s) => <button key={s} type="button" className={`sz${width === s ? ' on' : ''}`} aria-label={`${s} px`} onClick={() => setWidth(s)}><i style={{ width: s + 4, height: s + 4 }} /></button>)}</div>
        <button type="button" className={`a-btn sm ghost${eraser ? ' on' : ''}`} onClick={() => setEraser((v) => !v)}>Radír</button>
        <button type="button" className="a-btn sm ghost" disabled={!undoN} onClick={doUndo} title="Ctrl+Z">Visszavonás</button>
        <button type="button" className="a-btn sm ghost" onClick={clear}>Törlés</button>
        <button type="button" className="a-btn sm ghost" onClick={() => void pasteBg()} title="A vágólapon lévő kép lesz a háttér (Ctrl+V is)">Háttér: vágólap</button>
        <span className="a-draw-hint">{bgName ? `háttér: ${bgName} · ` : ''}{size.w}×{size.h} · Esc = mégse</span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" className="a-btn sm ghost" onClick={onCancel}>Mégse</button>
          <button type="button" className="a-btn sm solid" onClick={finish}>Kész — a mezőhöz csatol</button>
        </div>
      </div>
      <div className="a-draw-area" ref={areaRef}>
        <div className="a-draw-stage" style={{ width: fit.w, height: fit.h }}>
          <canvas ref={bgRef} width={size.w} height={size.h} aria-hidden="true" />
          <canvas ref={drawRef} width={size.w} height={size.h} className={eraser ? 'eraser' : ''} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
